import * as React from "react"
import { Link, useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import { LogOut, Building2, MapPin, Box, Scan, ClipboardList, User, Tag, LayoutDashboard } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNotifications } from "@/hooks/useNotifications"
import { MiniReminders } from "./MiniReminders"

interface HeaderProps {
  onSignOut: () => void;
}

export function Header({ onSignOut }: HeaderProps) {
  const location = useLocation()
  const { t } = useTranslation()
  const { todayRecords, todayReminders } = useNotifications()
  
  return (
    <header className="hidden md:flex flex-col fixed left-0 top-0 h-screen w-64 border-r bg-white/80 backdrop-blur-lg">
      <div className="p-6 border-b shrink-0">
        <Link 
          to="/" 
          className="inline-flex px-4 py-2 rounded-full text-sm font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
        >
          Tivo Asset Manager
        </Link>
      </div>
      
      <nav className="flex-1 overflow-y-auto p-4">
        <ul className="flex flex-col space-y-4">
          <li>
            <Link 
              to="/"
              className={cn(
                "w-full text-sm font-medium transition-colors inline-flex items-center space-x-3 p-2 rounded-lg",
                location.pathname === "/"
                  ? "text-apple-blue bg-apple-blue/10"
                  : "text-gray-600 hover:text-apple-blue hover:bg-gray-100"
              )}
            >
              <LayoutDashboard className="h-5 w-5" />
              <span>{t('menu.overview')}</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/scan"
              className={cn(
                "w-full text-sm font-medium transition-colors inline-flex items-center space-x-3 p-2 rounded-lg",
                location.pathname.includes("/scan")
                  ? "text-apple-blue bg-apple-blue/10"
                  : "text-gray-600 hover:text-apple-blue hover:bg-gray-100"
              )}
            >
              <Scan className="h-5 w-5" />
              <span>{t('menu.scan')}</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/records"
              className={cn(
                "w-full text-sm font-medium transition-colors inline-flex items-center space-x-3 p-2 rounded-lg",
                location.pathname.includes("/records")
                  ? "text-apple-blue bg-apple-blue/10"
                  : "text-gray-600 hover:text-apple-blue hover:bg-gray-100"
              )}
            >
              <div className="relative">
                <ClipboardList className="h-5 w-5" />
                {todayRecords > 0 && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full" />
                )}
              </div>
              <span>{t('menu.records')}</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/assets"
              className={cn(
                "w-full text-sm font-medium transition-colors inline-flex items-center space-x-3 p-2 rounded-lg",
                location.pathname.includes("/assets")
                  ? "text-apple-blue bg-apple-blue/10"
                  : "text-gray-600 hover:text-apple-blue hover:bg-gray-100"
              )}
            >
              <Box className="h-5 w-5" />
              <span>{t('menu.assets')}</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/places"
              className={cn(
                "w-full text-sm font-medium transition-colors inline-flex items-center space-x-3 p-2 rounded-lg",
                location.pathname.includes("/places")
                  ? "text-apple-blue bg-apple-blue/10"
                  : "text-gray-600 hover:text-apple-blue hover:bg-gray-100"
              )}
            >
              <MapPin className="h-5 w-5" />
              <span>{t('menu.places')}</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/companies"
              className={cn(
                "w-full text-sm font-medium transition-colors inline-flex items-center space-x-3 p-2 rounded-lg",
                location.pathname.includes("/companies")
                  ? "text-apple-blue bg-apple-blue/10"
                  : "text-gray-600 hover:text-apple-blue hover:bg-gray-100"
              )}
            >
              <Building2 className="h-5 w-5" />
              <span>{t('menu.companies')}</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/tivo-tags"
              className={cn(
                "w-full text-sm font-medium transition-colors inline-flex items-center space-x-3 p-2 rounded-lg",
                location.pathname.includes("/tivo-tags")
                  ? "text-apple-blue bg-apple-blue/10"
                  : "text-gray-600 hover:text-apple-blue hover:bg-gray-100"
              )}
            >
              <Tag className="h-5 w-5" />
              <span>{t('menu.tivoTags')}</span>
            </Link>
          </li>
        </ul>
      </nav>

      <div className="border-t max-h-[30vh] overflow-y-auto">
        <MiniReminders />
      </div>

      <div className="p-4 border-t space-y-2 shrink-0">
        <Link 
          to="/profile"
          className={cn(
            "w-full text-sm font-medium transition-colors inline-flex items-center space-x-3 p-2 rounded-lg",
            location.pathname.includes("/profile")
              ? "text-apple-blue bg-apple-blue/10"
              : "text-gray-600 hover:text-apple-blue hover:bg-gray-100"
          )}
        >
          <User className="h-5 w-5" />
          <span>{t('menu.profile')}</span>
        </Link>
        <button 
          onClick={onSignOut}
          className="w-full inline-flex items-center space-x-3 p-2 rounded-lg text-gray-600 hover:text-apple-blue hover:bg-gray-100"
        >
          <LogOut className="h-5 w-5" />
          <span className="text-sm font-medium">{t('menu.logout')}</span>
        </button>
      </div>
    </header>
  )
} 